import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserStats, UserStatsResponse, getUserUsageStats, UserUsageStatsResponse } from '../api/admin';
import Navigation from '../components/Navigation';

const UserDetails: React.FC = () => {
  const { email } = useParams<{ email: string }>();
  const navigate = useNavigate();
  const [stats, setStats] = useState<UserStatsResponse | null>(null);
  const [usageStats, setUsageStats] = useState<UserUsageStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!email) {
      navigate('/admin');
      return;
    }

    const fetchUserStats = async () => {
      setLoading(true);
      setError('');
      try {
        const [userStatsData, userUsageData] = await Promise.all([
          getUserStats(decodeURIComponent(email)),
          getUserUsageStats(decodeURIComponent(email))
        ]);
        setStats(userStatsData);
        setUsageStats(userUsageData);
      } catch (err) {
        setError('Failed to load user statistics');
        console.error('Error fetching user stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserStats();
  }, [email, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0b0f]">
        <Navigation />
        <div className="flex justify-center items-center h-96">
          <div className="text-zinc-500">Loading user details...</div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="min-h-screen bg-[#0a0b0f]">
        <Navigation />
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => navigate('/admin')}
              className="btn-secondary mb-4 px-4 py-2 text-sm rounded-lg"
            >
              ← Back to Admin Dashboard
            </button>
            <div className="alert-error">
              {error || 'User not found'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-[#0a0b0f]">
      <Navigation />
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/admin')}
              className="btn-secondary mb-4 px-4 py-2 text-sm rounded-lg"
            >
              ← Back to Admin Dashboard
            </button>
            <h1 className="text-2xl font-semibold text-zinc-100">User Details</h1>
            <p className="text-zinc-500">{stats.user_info.email}</p>
          </div>

          {/* User Info Card */}
          <div className="card-dark rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-zinc-100 mb-4">User Information</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-zinc-500">Email</p>
                <p className="font-medium text-zinc-100">{stats.user_info.email}</p>
              </div>
              <div>
                <p className="text-sm text-zinc-500">Role</p>
                <p className="font-medium">
                  {stats.user_info.admin ? (
                    <span className="text-amber-400">Admin</span>
                  ) : (
                    <span className="text-zinc-300">User</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-zinc-500">Subscription</p>
                <span className={`badge ${
                  stats.user_info.subscription_active ? 'badge-success' : 'badge-danger'
                }`}>
                  {stats.user_info.subscription_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <p className="text-sm text-zinc-500">Claims Tier</p>
                <p className="font-medium text-zinc-100 capitalize">{stats.user_info.claims_tier}</p>
              </div>
              <div>
                <p className="text-sm text-zinc-500">Member Since</p>
                <p className="font-medium text-zinc-100">{formatDateShort(stats.user_info.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-zinc-500">Last Activity</p>
                <p className="font-medium text-zinc-100">{formatDate(stats.statistics.last_activity)}</p>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="stat-card">
              <p className="stat-card-label">Total Images</p>
              <p className="stat-card-value text-blue-400">{stats.statistics.total_images}</p>
              <p className="stat-card-sub">{stats.statistics.recent_uploads_7d} in last 7 days</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-label">Total Locations</p>
              <p className="stat-card-value text-emerald-400">{stats.statistics.total_locations}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-label">Storage Used</p>
              <p className="stat-card-value text-purple-400">{stats.statistics.storage_used_mb.toFixed(1)} MB</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-label">Total Downloads</p>
              <p className="stat-card-value text-orange-400">{stats.statistics.total_downloads}</p>
            </div>
          </div>

          {/* API Usage Statistics */}
          {usageStats && (
            <>
              <h3 className="text-lg font-semibold text-zinc-100 mb-4">API Usage Statistics</h3>

              {/* Usage Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="stat-card">
                  <p className="stat-card-label">Total API Calls</p>
                  <p className="stat-card-value text-indigo-400">{usageStats.total_requests.toLocaleString()}</p>
                  <p className="stat-card-sub">{usageStats.requests_today} today</p>
                </div>
                <div className="stat-card">
                  <p className="stat-card-label">Total Cost</p>
                  <p className="stat-card-value text-red-400">${usageStats.total_cost.toFixed(2)}</p>
                  <p className="stat-card-sub">${usageStats.cost_today.toFixed(2)} today</p>
                </div>
                <div className="stat-card">
                  <p className="stat-card-label">Total Tokens</p>
                  <p className="stat-card-value text-teal-400">{usageStats.total_tokens.toLocaleString()}</p>
                </div>
                <div className="stat-card">
                  <p className="stat-card-label">Last API Call</p>
                  <p className="text-sm font-medium text-zinc-300">
                    {usageStats.last_request_time ? formatDate(usageStats.last_request_time) : 'Never'}
                  </p>
                </div>
              </div>

              {/* Service Breakdown */}
              <div className="card-dark rounded-xl p-6 mb-6">
                <h4 className="text-lg font-semibold text-zinc-100 mb-4">Usage by Service</h4>
                {usageStats.total_requests > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="table-dark">
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th>Requests</th>
                          <th>Tokens</th>
                          <th>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(usageStats.requests_by_service).map(([service, count]) => (
                          <tr key={service}>
                            <td>
                              <span className="badge badge-info capitalize">{service}</span>
                            </td>
                            <td>{count.toLocaleString()}</td>
                            <td>{(usageStats.tokens_by_service[service] || 0).toLocaleString()}</td>
                            <td className="font-medium">${(usageStats.cost_by_service[service] || 0).toFixed(4)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-zinc-500">No API usage yet</p>
                )}
              </div>
            </>
          )}

          {/* Two column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Images by Status */}
            <div className="card-dark rounded-xl p-6">
              <h2 className="text-lg font-semibold text-zinc-100 mb-4">Images by Status</h2>
              {stats.statistics.images_by_status && Object.entries(stats.statistics.images_by_status).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(stats.statistics.images_by_status).map(([status, count]) => (
                    <div key={status} className="flex justify-between items-center">
                      <span className="text-zinc-400 capitalize">{status}</span>
                      <span className="font-medium text-zinc-100">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500">No images uploaded yet</p>
              )}
            </div>

            {/* Device Information */}
            <div className="card-dark rounded-xl p-6">
              <h2 className="text-lg font-semibold text-zinc-100 mb-4">Device Information</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400">Registered Devices</span>
                  <span className="font-medium text-zinc-100">{stats.statistics.total_devices}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Locations Table */}
          <div className="card-dark rounded-xl p-6 mt-6">
            <h2 className="text-lg font-semibold text-zinc-100 mb-4">Locations</h2>
            {stats.locations_data && stats.locations_data.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="table-dark">
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>Image Count</th>
                      <th>Last Upload</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.locations_data.map((loc, index) => (
                      <tr key={index}>
                        <td>{loc.location}</td>
                        <td>{loc.image_count}</td>
                        <td className="text-zinc-400">{formatDate(loc.last_upload)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-zinc-500">No locations found</p>
            )}
          </div>

          {/* Recent Activity */}
          <div className="card-dark rounded-xl p-6 mt-6">
            <h2 className="text-lg font-semibold text-zinc-100 mb-4">Recent Activity</h2>
            {stats.recent_activity && stats.recent_activity.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_activity.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3 py-2 border-b border-zinc-800 last:border-0">
                    <div className="flex-1">
                      <p className="text-sm text-zinc-200">{activity.description}</p>
                      <p className="text-xs text-zinc-500">{formatDate(activity.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
